/**
 * A sign-in refused because the client is blocked or deleted, or the user is blocked (#2610066).
 *
 * IRES Cloud refuses every sign-in path with a reason (IRES Cloud `functions/docs/users/sign-in-refusal.md`);
 * IRES Office shows it on the login screen. A session already running when its client or user gets
 * blocked is signed out with the same message: the reason is held here in memory across the sign-out,
 * because the session storage is cleared with it and the page does not reload.
 */

/** Why a sign-in was refused, as IRES Cloud sends it. */
export type SignInRefusalReason = "CLIENT_BLOCKED" | "USER_BLOCKED";

const REASONS: SignInRefusalReason[] = ["CLIENT_BLOCKED", "USER_BLOCKED"];

/** The reason in a server reply or a route query, or null for anything else. */
export function toSignInRefusalReason(value: unknown): SignInRefusalReason | null {
  return REASONS.includes(value as SignInRefusalReason) ? (value as SignInRefusalReason) : null;
}

/** The i18n key of the message for a refusal. */
export function signInRefusalMessageKey(reason: SignInRefusalReason): string {
  return `login.signInRefused.${reason}`;
}

/**
 * Whether a client can no longer be signed in to: blocked, or deleted, which counts as blocked
 * (Taimoor, 2026-10-14). Only a real `true` counts, as in IRES Cloud.
 */
export function isClientSignInBlocked(client: { blocked?: unknown; deleted?: unknown } | null | undefined): boolean {
  return client?.blocked === true || client?.deleted === true;
}

let pendingRefusal: SignInRefusalReason | null = null;

/** Holds the reason for the login screen across the sign-out that follows. */
export function rememberSignInRefusal(reason: SignInRefusalReason): void {
  pendingRefusal = reason;
}

/** The reason held by {@link rememberSignInRefusal}, once: reading it clears it. */
export function takeSignInRefusal(): SignInRefusalReason | null {
  const reason = pendingRefusal;
  pendingRefusal = null;
  return reason;
}
